import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { clearedImpact, getBlackoutImpact, getBlackoutTargets } from "store/blackouts"
import { BLACKOUT_SCOPES, IMPACT_DEBOUNCE_MS } from "../../constants"
import { toRecurrence } from "helpers/blackouts"
import {
  buildBlackoutPayload,
  createEmptyEntry,
  getEntryErrors,
  getRecurrenceError,
  getSavedEntries,
  toEntriesPayload,
  toRecurrencePayload,
  toSingleDateEntries,
  withRowIds
} from "../../utils"

/**
 * A stable string for the parts of the form the guide can actually change, used to tell a
 * pristine form from an edited one.
 *
 * Entry ids are left out: a newly added row gets a client-generated id, so comparing ids would
 * report every form as dirty. Target ids are sorted and stringified because the pickers return
 * them in selection order, and the API mixes numbers and numeric strings.
 */
const toComparableState = ({ reason, recurrence, entries, selectedIds }) =>
  JSON.stringify({
    reason: reason?.trim() || "",
    recurrence: recurrence ? [recurrence.frequency, recurrence.interval, [...recurrence.weekdays].sort(), recurrence.ends_on] : null,
    entries: (entries || []).map((entry) => [entry.start_date, entry.end_date, entry.start_time, entry.end_time]),
    targets: (selectedIds || []).map(String).sort()
  })

/**
 * Form state for one blackout, plus the debounced impact preview.
 *
 * `blackout` is null when adding. Target options are fetched per scope and cached in the
 * store, so reopening the modal for the same scope does not refetch.
 */
export const useBlackoutForm = ({ scope, blackout, isOpen }) => {
  const dispatch = useDispatch()

  const isBusinessScope = scope === BLACKOUT_SCOPES.BUSINESS

  const [reason, setReason] = useState("")
  const [entries, setEntries] = useState([createEmptyEntry()])
  /** null when the blackout does not repeat. */
  const [recurrence, setRecurrence] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])

  const targetOptions = useSelector((state) => state.blackouts?.targets?.data?.[scope] || [])
  const isLoadingTargets = useSelector((state) => Boolean(state.blackouts?.targets?.loading))
  const impact = useSelector((state) => state.blackouts?.impact?.data)
  const isFetchingImpact = useSelector((state) => Boolean(state.blackouts?.impact?.loading))

  /**
   * True from the moment a target or date changes until the impact request settles — the store's
   * own flag only covers the request, leaving the debounce window showing a stale count.
   */
  const [isImpactPending, setIsImpactPending] = useState(false)
  const isLoadingImpact = isImpactPending || isFetchingImpact

  /** The seeded state, as a comparable string — what `isDirty` measures against. */
  const pristineStateRef = useRef("")

  // Seed the form whenever it opens, so a reopened modal never shows the previous values.
  useEffect(() => {
    if (!isOpen) return
    const seeded = {
      reason: blackout?.reason || "",
      recurrence: toRecurrence(blackout),
      // Saved rows keep their server ids when those are distinct — see the note on `withRowIds`.
      entries: blackout?.entries?.length ? withRowIds(getSavedEntries(blackout)) : [createEmptyEntry()],
      selectedIds: blackout?.target_ids || []
    }
    setReason(seeded.reason)
    setEntries(seeded.entries)
    setRecurrence(seeded.recurrence)
    setSelectedIds(seeded.selectedIds)
    pristineStateRef.current = toComparableState(seeded)
  }, [isOpen, blackout])

  useEffect(() => {
    if (isOpen && !isBusinessScope) dispatch(getBlackoutTargets(scope))
  }, [dispatch, isOpen, scope, isBusinessScope])

  useEffect(() => {
    if (!isOpen) dispatch(clearedImpact())
  }, [dispatch, isOpen])

  const datedEntries = useMemo(() => entries.filter((entry) => entry.start_date), [entries])

  // Re-price the impact as the guide edits. Debounced because it fires on every keystroke
  // of the pickers and on every checkbox toggle.
  useEffect(() => {
    if (!isOpen) return undefined
    if (!isBusinessScope && !selectedIds.length) {
      setIsImpactPending(false)
      dispatch(clearedImpact())
      return undefined
    }

    setIsImpactPending(true)
    let isStale = false

    const timeoutId = setTimeout(async () => {
      try {
        // The repeat rule is sent so the count can cover bookings on the repeated dates too.
        await dispatch(
          getBlackoutImpact({
            scope,
            target_ids: selectedIds,
            entries: toEntriesPayload(datedEntries, recurrence),
            recurrence: toRecurrencePayload(recurrence)
          })
        )
      } finally {
        // A newer edit has already flagged its own pending state — don't clear it.
        if (!isStale) setIsImpactPending(false)
      }
    }, IMPACT_DEBOUNCE_MS)

    return () => {
      isStale = true
      clearTimeout(timeoutId)
    }
  }, [dispatch, isOpen, scope, isBusinessScope, selectedIds, datedEntries, recurrence])

  /**
   * Turning Repeat on narrows "Applies on" to the first date, so what the form shows is what will
   * repeat — see `AppliesOnRows`. Turning it off leaves that one row as it is.
   */
  const changeRecurrence = useCallback(
    (nextRecurrence) => {
      if (nextRecurrence && !recurrence) setEntries((current) => toSingleDateEntries(current))
      setRecurrence(nextRecurrence)
    },
    [recurrence]
  )

  const entryErrors = useMemo(() => getEntryErrors(entries), [entries])
  const recurrenceError = useMemo(() => getRecurrenceError(recurrence, entries), [recurrence, entries])

  const isValid = useMemo(() => {
    const hasDates = datedEntries.length > 0
    const hasTargets = isBusinessScope || selectedIds.length > 0
    return hasDates && hasTargets && Object.keys(entryErrors).length === 0 && !recurrenceError
  }, [datedEntries, entryErrors, recurrenceError, isBusinessScope, selectedIds])

  const isDirty = useMemo(
    () => toComparableState({ reason, recurrence, entries, selectedIds }) !== pristineStateRef.current,
    [reason, recurrence, entries, selectedIds]
  )

  const buildPayload = useCallback(
    () => buildBlackoutPayload({ scope, reason, recurrence, entries, selectedIds }),
    [scope, reason, recurrence, entries, selectedIds]
  )

  return {
    reason,
    setReason,
    entries,
    setEntries,
    recurrence,
    setRecurrence: changeRecurrence,
    recurrenceError,
    selectedIds,
    setSelectedIds,
    targetOptions,
    isLoadingTargets,
    impact,
    isLoadingImpact,
    isValid,
    isDirty,
    entryErrors,
    buildPayload
  }
}

export default useBlackoutForm
